import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { WebPageSchema } from '@/components/base/SchemaOrg';

const ARTICLES_PER_PAGE = 6;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);
  const [page, setPage] = useState(1);

  const category = categories.find((c) => c.slug === activeCategory) || categories[0];

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const pagedArticles = filteredArticles.slice(
    (page - 1) * ARTICLES_PER_PAGE,
    page * ARTICLES_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setPage(1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 年轻的妈妈育儿指南`}
        description={`${category.description}。专为年轻的妈妈打造的${category.name}知识库，涵盖从基础到进阶的全面指导。`}
        keywords={`${category.name},育儿,年轻的妈妈,新手妈妈,${category.name}技巧`}
        canonicalUrl={`https://young-mama.com/category/${activeCategory}`}
      />
      <WebPageSchema
        title={`${category.name} - 年轻的妈妈育儿指南`}
        description={`${category.description}。专为年轻的妈妈打造的${category.name}知识库。`}
        url={`https://young-mama.com/category/${activeCategory}`}
      />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          {/* Category Header */}
          <div className="py-8 md:py-12 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
              {category.name}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              {category.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-10">
            {pagedArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <h2 className="font-heading font-semibold text-base text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h2>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400 pt-3 border-t border-background-200/70">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {article.readTime} 分钟
                    </span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="flex items-center justify-center gap-2 pb-14" aria-label="分页导航">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    p === page
                      ? 'bg-primary-500 text-white'
                      : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </nav>
          )}

          {filteredArticles.length === 0 && (
            <div className="text-center py-20 text-foreground-500">
              <p>该分类暂无文章，敬请期待！</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}